/**
 * SitePick front — 取置きページ（/hold/…）・再販通知ページ（/restock/…）の補助（reservation-notify §9）。
 *
 * ページのフォームは JS 無しで動くプレーンな POST（PRG はプラグインの Router）。ここは上乗せだけ:
 * - 「共有用リンクをコピー」（`clipboard.js` の copyText()。失敗したらリンクの入力欄を選択状態にする）
 * - `data-sp-confirm` の付いたフォームは送信前に確認ダイアログを出す（共有リンクの作り直し）
 * - 二重送信の抑止（送信後はボタンを押せなくする）
 * main.js のバンドルに入り、`.js-sp-hold-page` が無いページでは何もしない（onboarding.js と同じ型）。
 */

import { announce } from "./live-region.js";
import { copyText } from "./clipboard.js";

function msg(key, fallback) {
  const m = (window.SITEPICK && window.SITEPICK.messages) || {};
  return typeof m[key] === "string" && m[key] ? m[key] : fallback;
}

/**
 * @param {HTMLElement} page
 * @returns {void}
 */
function initCopy(page) {
  page.querySelectorAll(".js-sp-copy").forEach((button) => {
    if (!(button instanceof HTMLButtonElement)) {
      return;
    }
    const targetId = button.getAttribute("data-sp-copy-target") || "";
    const input = targetId ? document.getElementById(targetId) : null;
    if (!(input instanceof HTMLInputElement)) {
      return;
    }
    const status = page.querySelector(`[data-sp-copy-status="${targetId}"]`);
    button.hidden = false;
    button.addEventListener("click", async () => {
      const ok = await copyText(input.value);
      const text = ok
        ? msg("copyDone", "共有用リンクをコピーしました")
        : msg("copyFailed", "コピーできませんでした。リンクを選択しましたので、長押しまたは右クリックでコピーしてください。");
      if (!ok) {
        input.focus();
        input.select();
      }
      if (status instanceof HTMLElement) {
        status.textContent = text;
        status.hidden = false;
      }
      announce(text);
    });
  });

  // 入力欄をタップしたら全体を選択（手動コピーしやすく）。
  page.querySelectorAll(".js-sp-copy-source").forEach((input) => {
    if (input instanceof HTMLInputElement) {
      input.addEventListener("focus", () => input.select());
    }
  });
}

/**
 * @param {HTMLElement} page
 * @returns {void}
 */
function initForms(page) {
  page.querySelectorAll("form.js-sp-hold-page-form").forEach((form) => {
    if (!(form instanceof HTMLFormElement)) {
      return;
    }
    form.addEventListener("submit", (event) => {
      if (form.dataset.spSubmitted === "1") {
        event.preventDefault();
        return;
      }
      const question = form.getAttribute("data-sp-confirm") || "";
      if (question && !window.confirm(question)) {
        event.preventDefault();
        return;
      }
      form.dataset.spSubmitted = "1";
      form.classList.add("is-loading");
      // 送信そのものは止めない（ボタンの値は使わない。op は hidden で送る）。
      window.setTimeout(() => {
        form.querySelectorAll("button[type=\"submit\"]").forEach((button) => {
          if (button instanceof HTMLButtonElement) {
            button.disabled = true;
            button.setAttribute("aria-busy", "true");
          }
        });
      }, 0);
    });
  });

  // bfcache で戻ったときは押せる状態に戻す。
  window.addEventListener("pageshow", (event) => {
    if (!event.persisted) {
      return;
    }
    page.querySelectorAll("form.js-sp-hold-page-form").forEach((form) => {
      if (!(form instanceof HTMLFormElement)) {
        return;
      }
      form.dataset.spSubmitted = "";
      form.classList.remove("is-loading");
      form.querySelectorAll("button[type=\"submit\"]").forEach((button) => {
        if (button instanceof HTMLButtonElement && !button.hasAttribute("data-sp-keep-disabled")) {
          button.disabled = false;
          button.removeAttribute("aria-busy");
        }
      });
    });
  });
}

/**
 * @returns {void}
 */
export function initHoldPage() {
  const page = document.querySelector(".js-sp-hold-page");
  if (!(page instanceof HTMLElement)) {
    return;
  }
  initCopy(page);
  initForms(page);
}
